import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import type { ChangeEvent, KeyboardEvent, SVGProps } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

// Şirket-içi Mesajlaşma + Özel E-mail — suite genelinde paylaşılan widget. Backend +
// veriler Algebra'da; bu widget algebra-api'yi MUTLAK URL ile (CORS açık) çağırır.
// Auth, .isosofts.com'da paylaşılan auth_token cookie'sinden. Self-contained.
const ALG = 'https://algebra.isosofts.com/api';

const getToken = (): string =>
  document.cookie.split('; ').find((r) => r.startsWith('auth_token='))?.split('=').slice(1).join('=') ?? '';

interface Contact {
  id: string; name?: string; surname?: string; email?: string; avatarPath?: string;
  unreadCount?: number; lastMessageAt?: string; lastMessagePreview?: string;
}
interface Msg { id: string; senderId: string; content: string; isRead?: number; createdAt: string; }
interface Recipient { id: string; name?: string; surname?: string; email?: string; }

type Dict = Record<string, string>;
const STRINGS: Record<string, Dict> = {
  en: {
    messages: 'Messages', email: 'Send Email', search: 'Search colleagues…', noContacts: 'No colleagues found',
    selectContact: 'Select a colleague to start chatting', emptyChat: 'No messages yet — say hi!',
    typeMsg: 'Type a message…', errSend: 'Failed to send message', read: 'Read', sent: 'Sent',
    emailSubtitle: 'Write a custom email and send it to selected colleagues.', subject: 'Subject',
    subjectPh: 'Email subject', messagePh: 'Write your message…', recipients: 'Recipients',
    searchPeople: 'Search people…', selectAll: 'Select all', clear: 'Clear', noRecipients: 'No people found',
    send: 'Send', sending: 'Sending…', noAccess: "You don't have permission to send emails.",
    sentOk: 'Email sent', errFields: 'Subject and message are required', errPick: 'Select at least one recipient',
    help: 'Help & Support', helpSubtitle: 'Found a bug or need help? Send a ticket to the IsoSofts team.',
    ticketType: 'Type', typeBug: 'Bug / Error', typeQuestion: 'Question / How-to', typeFeature: 'Feature request', typeOther: 'Other',
    ticketSubject: 'Subject', ticketSubjectPh: 'Short summary of the issue', ticketMessage: 'Description',
    ticketMessagePh: 'What happened? What did you expect? Steps to reproduce…',
    ticketFiles: 'Screenshots / files', ticketAddFiles: 'Add images', ticketFilesHint: 'Up to 6 files (PNG, JPG, GIF, PDF) · max 8 MB each',
    ticketWho: 'Your name, e-mail and company are sent together with the ticket.',
    ticketSend: 'Send ticket', ticketSending: 'Sending…', ticketRemove: 'Remove',
    ticketOk: 'Ticket sent — thank you! Reference:', ticketErr: 'Ticket could not be sent. Please try again.',
    ticketErrFields: 'Subject and description are required', ticketErrMax: 'You can attach up to 6 files',
    ticketErrType: 'Unsupported file type', ticketErrSize: 'File is larger than 8 MB',
    ticketRate: 'Too many tickets sent. Please try again later.',
    helpShort: 'Create Help Ticket', tabNew: 'New ticket', tabMine: 'My tickets', tabAll: 'All tickets',
    listEmpty: 'No tickets yet',
    stSent: 'Sent', stRead: 'Read by IT', stContact: 'We will contact you',
    stInProgress: 'In progress', stResolved: 'Resolved', stClosed: 'Closed',
    detailBack: 'Back', detailOpened: 'Opened', detailReporter: 'Reported by', detailEmail: 'E-mail',
    detailCompany: 'Company', detailApp: 'Application', detailPage: 'Page',
    detailUpdatedBy: 'Updated by', detailStatusNote: 'Note from support',
    agentUpdate: 'Update status', agentSave: 'Save', agentSaved: 'Status updated',
    agentNotePh: 'Optional note for the reporter…', agentMailHint: 'The reporter is notified by e-mail.',
  },
  az: {
    messages: 'Mesajlar', email: 'E-poçt Göndər', search: 'Əməkdaş axtar…', noContacts: 'Əməkdaş tapılmadı',
    selectContact: 'Söhbətə başlamaq üçün bir əməkdaş seçin', emptyChat: 'Hələ mesaj yoxdur — salam deyin!',
    typeMsg: 'Mesaj yazın…', errSend: 'Mesaj göndərilə bilmədi', read: 'Oxundu', sent: 'Göndərildi',
    emailSubtitle: 'Xüsusi bir e-poçt yazıb seçdiyiniz əməkdaşlara göndərin.', subject: 'Mövzu',
    subjectPh: 'E-poçt mövzusu', messagePh: 'Mesajınızı yazın…', recipients: 'Alıcılar',
    searchPeople: 'Şəxs axtar…', selectAll: 'Hamısını seç', clear: 'Təmizlə', noRecipients: 'Şəxs tapılmadı',
    send: 'Göndər', sending: 'Göndərilir…', noAccess: 'E-poçt göndərmək icazəniz yoxdur.',
    sentOk: 'E-poçt göndərildi', errFields: 'Mövzu və mesaj tələb olunur', errPick: 'Ən azı bir alıcı seçin',
    help: 'Yardım və Dəstək', helpSubtitle: 'Xəta tapdınız və ya köməyə ehtiyacınız var? IsoSofts komandasına müraciət göndərin.',
    ticketType: 'Növ', typeBug: 'Xəta / Problem', typeQuestion: 'Sual / Necə edim', typeFeature: 'Yeni imkan təklifi', typeOther: 'Digər',
    ticketSubject: 'Mövzu', ticketSubjectPh: 'Problemin qısa təsviri', ticketMessage: 'Təsvir',
    ticketMessagePh: 'Nə baş verdi? Nə gözləyirdiniz? Hansı addımlardan sonra?…',
    ticketFiles: 'Ekran görüntüsü / fayllar', ticketAddFiles: 'Şəkil əlavə et', ticketFilesHint: 'Ən çox 6 fayl (PNG, JPG, GIF, PDF) · hər biri maks. 8 MB',
    ticketWho: 'Adınız, e-poçtunuz və şirkətiniz müraciətlə birlikdə göndərilir.',
    ticketSend: 'Müraciəti göndər', ticketSending: 'Göndərilir…', ticketRemove: 'Sil',
    ticketOk: 'Müraciət göndərildi — təşəkkürlər! Nömrə:', ticketErr: 'Müraciət göndərilə bilmədi. Yenidən cəhd edin.',
    ticketErrFields: 'Mövzu və təsvir tələb olunur', ticketErrMax: 'Ən çox 6 fayl əlavə edə bilərsiniz',
    ticketErrType: 'Dəstəklənməyən fayl tipi', ticketErrSize: 'Fayl 8 MB-dan böyükdür',
    ticketRate: 'Çox sayda müraciət göndərildi. Bir az sonra yenidən cəhd edin.',
    helpShort: 'Yardım Müraciəti Yarat', tabNew: 'Yeni müraciət', tabMine: 'Müraciətlərim', tabAll: 'Bütün müraciətlər',
    listEmpty: 'Hələ müraciət yoxdur',
    stSent: 'Göndərildi', stRead: 'IT tərəfindən oxundu', stContact: 'Sizinlə əlaqə saxlanılacaq',
    stInProgress: 'Üzərində işlənilir', stResolved: 'Həll olundu', stClosed: 'Bağlandı',
    detailBack: 'Geri', detailOpened: 'Açılıb', detailReporter: 'Göndərən', detailEmail: 'E-poçt',
    detailCompany: 'Şirkət', detailApp: 'Tətbiq', detailPage: 'Səhifə',
    detailUpdatedBy: 'Yeniləyən', detailStatusNote: 'Dəstək qeydi',
    agentUpdate: 'Statusu dəyiş', agentSave: 'Yadda saxla', agentSaved: 'Status yeniləndi',
    agentNotePh: 'Göndərən üçün qeyd (istəyə bağlı)…', agentMailHint: 'Göndərənə e-poçtla məlumat verilir.',
  },
  ru: {
    messages: 'Сообщения', email: 'Отправить e-mail', search: 'Поиск коллег…', noContacts: 'Коллеги не найдены',
    selectContact: 'Выберите коллегу, чтобы начать чат', emptyChat: 'Сообщений пока нет — поздоровайтесь!',
    typeMsg: 'Введите сообщение…', errSend: 'Не удалось отправить сообщение', read: 'Прочитано', sent: 'Отправлено',
    emailSubtitle: 'Напишите письмо и отправьте выбранным коллегам.', subject: 'Тема',
    subjectPh: 'Тема письма', messagePh: 'Введите сообщение…', recipients: 'Получатели',
    searchPeople: 'Поиск людей…', selectAll: 'Выбрать всех', clear: 'Очистить', noRecipients: 'Никого не найдено',
    send: 'Отправить', sending: 'Отправка…', noAccess: 'У вас нет прав на отправку писем.',
    sentOk: 'Письмо отправлено', errFields: 'Тема и сообщение обязательны', errPick: 'Выберите хотя бы одного получателя',
    help: 'Помощь и поддержка', helpSubtitle: 'Нашли ошибку или нужна помощь? Отправьте заявку команде IsoSofts.',
    ticketType: 'Тип', typeBug: 'Ошибка / Сбой', typeQuestion: 'Вопрос / Как сделать', typeFeature: 'Предложение', typeOther: 'Другое',
    ticketSubject: 'Тема', ticketSubjectPh: 'Краткое описание проблемы', ticketMessage: 'Описание',
    ticketMessagePh: 'Что произошло? Что вы ожидали? Шаги воспроизведения…',
    ticketFiles: 'Скриншоты / файлы', ticketAddFiles: 'Добавить изображения', ticketFilesHint: 'До 6 файлов (PNG, JPG, GIF, PDF) · не более 8 МБ каждый',
    ticketWho: 'Ваше имя, e-mail и компания отправляются вместе с заявкой.',
    ticketSend: 'Отправить заявку', ticketSending: 'Отправка…', ticketRemove: 'Удалить',
    ticketOk: 'Заявка отправлена — спасибо! Номер:', ticketErr: 'Не удалось отправить заявку. Попробуйте ещё раз.',
    ticketErrFields: 'Тема и описание обязательны', ticketErrMax: 'Можно приложить не более 6 файлов',
    ticketErrType: 'Неподдерживаемый тип файла', ticketErrSize: 'Файл больше 8 МБ',
    ticketRate: 'Отправлено слишком много заявок. Попробуйте позже.',
    helpShort: 'Создать заявку', tabNew: 'Новая заявка', tabMine: 'Мои заявки', tabAll: 'Все заявки',
    listEmpty: 'Заявок пока нет',
    stSent: 'Отправлено', stRead: 'Прочитано IT', stContact: 'Мы свяжемся с вами',
    stInProgress: 'В работе', stResolved: 'Решено', stClosed: 'Закрыто',
    detailBack: 'Назад', detailOpened: 'Создана', detailReporter: 'Отправитель', detailEmail: 'E-mail',
    detailCompany: 'Компания', detailApp: 'Приложение', detailPage: 'Страница',
    detailUpdatedBy: 'Обновил', detailStatusNote: 'Комментарий поддержки',
    agentUpdate: 'Изменить статус', agentSave: 'Сохранить', agentSaved: 'Статус обновлён',
    agentNotePh: 'Комментарий для отправителя (необязательно)…', agentMailHint: 'Отправитель получит уведомление по e-mail.',
  },
  lt: {
    messages: 'Žinutės', email: 'Siųsti el. laišką', search: 'Ieškoti kolegų…', noContacts: 'Kolegų nerasta',
    selectContact: 'Pasirinkite kolegą, kad pradėtumėte pokalbį', emptyChat: 'Žinučių dar nėra — pasisveikinkite!',
    typeMsg: 'Rašykite žinutę…', errSend: 'Nepavyko išsiųsti žinutės', read: 'Perskaityta', sent: 'Išsiųsta',
    emailSubtitle: 'Parašykite laišką ir išsiųskite pasirinktiems kolegoms.', subject: 'Tema',
    subjectPh: 'Laiško tema', messagePh: 'Įveskite žinutę…', recipients: 'Gavėjai',
    searchPeople: 'Ieškoti žmonių…', selectAll: 'Pažymėti visus', clear: 'Išvalyti', noRecipients: 'Žmonių nerasta',
    send: 'Siųsti', sending: 'Siunčiama…', noAccess: 'Neturite teisės siųsti laiškų.',
    sentOk: 'Laiškas išsiųstas', errFields: 'Tema ir žinutė privalomos', errPick: 'Pasirinkite bent vieną gavėją',
    help: 'Pagalba ir palaikymas', helpSubtitle: 'Radote klaidą ar reikia pagalbos? Išsiųskite užklausą IsoSofts komandai.',
    ticketType: 'Tipas', typeBug: 'Klaida / Triktis', typeQuestion: 'Klausimas / Kaip padaryti', typeFeature: 'Funkcijos pasiūlymas', typeOther: 'Kita',
    ticketSubject: 'Tema', ticketSubjectPh: 'Trumpas problemos aprašymas', ticketMessage: 'Aprašymas',
    ticketMessagePh: 'Kas nutiko? Ko tikėjotės? Atkūrimo žingsniai…',
    ticketFiles: 'Ekrano nuotraukos / failai', ticketAddFiles: 'Pridėti vaizdų', ticketFilesHint: 'Iki 6 failų (PNG, JPG, GIF, PDF) · po ne daugiau kaip 8 MB',
    ticketWho: 'Jūsų vardas, el. paštas ir įmonė siunčiami kartu su užklausa.',
    ticketSend: 'Siųsti užklausą', ticketSending: 'Siunčiama…', ticketRemove: 'Šalinti',
    ticketOk: 'Užklausa išsiųsta — ačiū! Numeris:', ticketErr: 'Nepavyko išsiųsti užklausos. Bandykite dar kartą.',
    ticketErrFields: 'Tema ir aprašymas privalomi', ticketErrMax: 'Galite pridėti iki 6 failų',
    ticketErrType: 'Nepalaikomas failo tipas', ticketErrSize: 'Failas didesnis nei 8 MB',
    ticketRate: 'Išsiųsta per daug užklausų. Bandykite vėliau.',
    helpShort: 'Sukurti užklausą', tabNew: 'Nauja užklausa', tabMine: 'Mano užklausos', tabAll: 'Visos užklausos',
    listEmpty: 'Užklausų dar nėra',
    stSent: 'Išsiųsta', stRead: 'Perskaitė IT', stContact: 'Susisieksime su jumis',
    stInProgress: 'Vykdoma', stResolved: 'Išspręsta', stClosed: 'Uždaryta',
    detailBack: 'Atgal', detailOpened: 'Sukurta', detailReporter: 'Siuntėjas', detailEmail: 'El. paštas',
    detailCompany: 'Įmonė', detailApp: 'Programa', detailPage: 'Puslapis',
    detailUpdatedBy: 'Atnaujino', detailStatusNote: 'Palaikymo komentaras',
    agentUpdate: 'Keisti būseną', agentSave: 'Išsaugoti', agentSaved: 'Būsena atnaujinta',
    agentNotePh: 'Komentaras siuntėjui (nebūtina)…', agentMailHint: 'Siuntėjas gaus pranešimą el. paštu.',
  },
};

const IconChat = (p: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" {...p}>
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  </svg>
);
const IconMail = (p: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" {...p}>
    <rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 6-10 7L2 6" />
  </svg>
);
const IconSend = (p: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" {...p}>
    <path d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7z" />
  </svg>
);

const IconHelp = (p: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" {...p}>
    <circle cx="12" cy="12" r="10" /><path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3" /><path d="M12 17h.01" />
  </svg>
);
const IconClip = (p: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" {...p}>
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  </svg>
);

const fullName = (c: Contact | Recipient): string => `${c.name ?? ''} ${c.surname ?? ''}`.trim() || (c.email ?? '');
const initials = (c: Contact): string =>
  (`${c.name?.[0] ?? ''}${c.surname?.[0] ?? ''}`.trim() || (c.email?.[0] ?? '?')).toUpperCase();
const timeShort = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
};

const Backdrop = ({ children, onClose }: { children: React.ReactNode; onClose: () => void }) => (
  <div className="fixed inset-0 z-[1000] bg-black/40 flex items-center justify-center p-3 sm:p-6" onClick={onClose}>
    {children}
  </div>
);

const SuiteTools = () => {
  const { i18n } = useTranslation();
  const lang = (i18n.language || 'en').slice(0, 2);
  const L = (k: string): string => STRINGS[lang]?.[k] ?? STRINGS.en[k];
  const token = getToken();

  const [open, setOpen] = useState<null | 'messages' | 'email' | 'help'>(null);
  const [msgUnread, setMsgUnread] = useState(0);
  const [canEmail, setCanEmail] = useState(false);

  useEffect(() => {
    if (!token) return;
    fetch(`https://isosofts.com/api/algebra/check-access?register=bulkemail&token=${token}`)
      .then((r) => setCanEmail(r.ok)).catch(() => setCanEmail(false));
    const load = () =>
      fetch(`${ALG}/message/unread-count?token=${token}`)
        .then((r) => (r.ok ? r.json() : { count: 0 }))
        .then((d: { count?: number }) => setMsgUnread(d?.count ?? 0)).catch(() => undefined);
    load();
    const id = window.setInterval(load, 60000);
    return () => window.clearInterval(id);
  }, [token]);

  if (!token) return null;

  return (
    <>
      <button onClick={() => setOpen('messages')} title={L('messages')} aria-label={L('messages')}
        className="relative flex items-center justify-center w-9 h-9 rounded-xl text-gray-600 hover:bg-blue-50 hover:text-blue-600 transition-colors">
        <IconChat />
        {msgUnread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center">
            {msgUnread > 99 ? '99+' : msgUnread}
          </span>
        )}
      </button>
      {canEmail && (
        <button onClick={() => setOpen('email')} title={L('email')} aria-label={L('email')}
          className="flex items-center justify-center w-9 h-9 rounded-xl text-gray-600 hover:bg-blue-50 hover:text-blue-600 transition-colors">
          <IconMail />
        </button>
      )}
      <button onClick={() => setOpen('help')} title={L('helpShort')} aria-label={L('helpShort')}
        className="flex items-center gap-1.5 h-9 px-2.5 rounded-xl text-gray-600 hover:bg-blue-50 hover:text-blue-600 transition-colors">
        <IconHelp />
        <span className="hidden md:inline text-xs font-semibold whitespace-nowrap">{L('helpShort')}</span>
      </button>
      {open === 'messages' && createPortal(<MessagesOverlay L={L} token={token} onClose={() => setOpen(null)} onReadChange={setMsgUnread} />, document.body)}
      {open === 'email' && createPortal(<EmailOverlay L={L} token={token} onClose={() => setOpen(null)} />, document.body)}
      {open === 'help' && createPortal(<HelpOverlay L={L} token={token} onClose={() => setOpen(null)} />, document.body)}
    </>
  );
};

interface OverlayProps { L: (k: string) => string; token: string; onClose: () => void; }

const MessagesOverlay = ({ L, token, onClose, onReadChange }: OverlayProps & { onReadChange: (n: number) => void }) => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [selected, setSelected] = useState<Contact | null>(null);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [search, setSearch] = useState('');
  const [sending, setSending] = useState(false);
  const threadRef = useRef<HTMLDivElement>(null);
  const selRef = useRef<Contact | null>(null);
  selRef.current = selected;

  const scrollDown = () => { if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight; };

  const fetchContacts = useCallback(() => {
    fetch(`${ALG}/message/contacts?token=${token}`).then((r) => (r.ok ? r.json() : []))
      .then((d: Contact[]) => setContacts(Array.isArray(d) ? d : [])).catch(() => undefined);
  }, [token]);

  const refreshUnread = useCallback(() => {
    fetch(`${ALG}/message/unread-count?token=${token}`).then((r) => (r.ok ? r.json() : { count: 0 }))
      .then((d: { count?: number }) => onReadChange(d?.count ?? 0)).catch(() => undefined);
  }, [token, onReadChange]);

  const fetchConv = useCallback((peerId: string, scroll: boolean) => {
    fetch(`${ALG}/message/conversation?withId=${peerId}&token=${token}`).then((r) => (r.ok ? r.json() : []))
      .then((d: Msg[]) => {
        if (selRef.current?.id !== peerId) return;
        setMessages(Array.isArray(d) ? d : []);
        if (scroll) window.setTimeout(scrollDown, 50);
      }).catch(() => undefined);
  }, [token]);

  useEffect(() => { fetchContacts(); const id = window.setInterval(fetchContacts, 10000); return () => window.clearInterval(id); }, [fetchContacts]);
  useEffect(() => {
    if (!selected) return;
    fetchConv(selected.id, true);
    window.setTimeout(() => { fetchContacts(); refreshUnread(); }, 700);
    const id = window.setInterval(() => fetchConv(selected.id, false), 3500);
    return () => window.clearInterval(id);
  }, [selected, fetchConv, fetchContacts, refreshUnread]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q ? contacts.filter((c) => fullName(c).toLowerCase().includes(q) || (c.email ?? '').toLowerCase().includes(q)) : contacts;
    return [...list].sort((a, b) => {
      if (a.lastMessageAt && b.lastMessageAt) return a.lastMessageAt < b.lastMessageAt ? 1 : -1;
      if (a.lastMessageAt) return -1; if (b.lastMessageAt) return 1;
      return fullName(a).localeCompare(fullName(b));
    });
  }, [contacts, search]);

  const send = () => {
    const text = input.trim();
    if (!text || !selected || sending) return;
    setSending(true);
    fetch(`${ALG}/message/send?token=${token}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ receiverId: selected.id, content: text }) })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((m: Msg) => { setInput(''); setMessages((p) => [...p, m]); window.setTimeout(scrollDown, 50); fetchContacts(); })
      .catch(() => window.alert(L('errSend'))).finally(() => setSending(false));
  };
  const removeMessage = (id: string) => { setMessages((p) => p.filter((m) => m.id !== id)); fetch(`${ALG}/message/${id}?token=${token}`, { method: 'DELETE' }).catch(() => undefined); };
  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } };

  return (
    <Backdrop onClose={onClose}>
      <div className="w-full max-w-5xl h-[85vh] bg-white rounded-2xl shadow-2xl flex overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className={`w-full sm:w-80 border-r border-gray-100 flex flex-col ${selected ? 'hidden sm:flex' : 'flex'}`}>
          <div className="p-3 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-base font-bold text-gray-800">{L('messages')}</h2>
            <button onClick={onClose} className="sm:hidden w-8 h-8 rounded-lg text-gray-500 hover:bg-gray-100 text-lg">✕</button>
          </div>
          <div className="p-2">
            <input value={search} onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)} placeholder={L('search')} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:bg-white" />
          </div>
          <div className="flex-1 overflow-y-auto">
            {filtered.length === 0 ? <div className="px-4 py-8 text-center text-sm text-gray-400">{L('noContacts')}</div> :
              filtered.map((c) => (
                <button key={c.id} onClick={() => { setSelected(c); setMessages([]); }} className={`w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 text-left ${selected?.id === c.id ? 'bg-blue-50' : ''}`}>
                  <span className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 text-white text-sm font-semibold flex items-center justify-center flex-shrink-0 overflow-hidden">{c.avatarPath ? <img src={`https://isosofts.com/api/account/avatar/${c.id}`} alt="" className="w-full h-full object-cover" /> : initials(c)}</span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold text-gray-800 truncate">{fullName(c)}</span>
                      {c.lastMessageAt && <span className="text-[10px] text-gray-400 flex-shrink-0">{timeShort(c.lastMessageAt)}</span>}
                    </span>
                    <span className="flex items-center justify-between gap-2">
                      <span className="text-xs text-gray-400 truncate">{c.lastMessagePreview || c.email}</span>
                      {(c.unreadCount ?? 0) > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">{(c.unreadCount ?? 0) > 99 ? '99+' : c.unreadCount}</span>}
                    </span>
                  </span>
                </button>
              ))}
          </div>
        </div>
        <div className={`flex-1 flex-col ${selected ? 'flex' : 'hidden sm:flex'}`}>
          <div className="p-3 border-b border-gray-100 flex items-center gap-3">
            {selected && <button onClick={() => setSelected(null)} className="sm:hidden w-8 h-8 rounded-lg text-gray-500 hover:bg-gray-100 text-lg">‹</button>}
            {selected ? (
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <span className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 text-white text-sm font-semibold flex items-center justify-center overflow-hidden">{selected.avatarPath ? <img src={`https://isosofts.com/api/account/avatar/${selected.id}`} alt="" className="w-full h-full object-cover" /> : initials(selected)}</span>
                <div className="min-w-0"><p className="text-sm font-semibold text-gray-800 truncate">{fullName(selected)}</p><p className="text-xs text-gray-400 truncate">{selected.email}</p></div>
              </div>
            ) : <div className="flex-1" />}
            <button onClick={onClose} className="hidden sm:block w-8 h-8 rounded-lg text-gray-500 hover:bg-gray-100 text-lg">✕</button>
          </div>
          {!selected ? (
            <div className="flex-1 flex items-center justify-center text-sm text-gray-400">{L('selectContact')}</div>
          ) : (
            <>
              <div ref={threadRef} className="flex-1 overflow-y-auto p-4 space-y-2 bg-gray-50/50">
                {messages.length === 0 ? <div className="h-full flex items-center justify-center text-sm text-gray-400">{L('emptyChat')}</div> :
                  messages.map((m) => {
                    const mine = m.senderId !== selected.id;
                    return (
                      <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'} group`}>
                        <div className={`max-w-[75%] flex items-end gap-1.5 ${mine ? 'flex-row-reverse' : ''}`}>
                          <div className={`px-3.5 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words ${mine ? 'bg-blue-600 text-white rounded-br-sm' : 'bg-white text-gray-800 border border-gray-100 rounded-bl-sm'}`}>
                            {m.content}
                            <span className={`block text-[10px] mt-1 text-right ${mine ? 'text-blue-100' : 'text-gray-400'}`}>
                              {timeShort(m.createdAt)}{mine && <span title={m.isRead ? L('read') : L('sent')} className="ml-1">{m.isRead ? '✓✓' : '✓'}</span>}
                            </span>
                          </div>
                          {mine && <button onClick={() => removeMessage(m.id)} className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 text-xs">🗑</button>}
                        </div>
                      </div>
                    );
                  })}
              </div>
              <div className="p-3 border-t border-gray-100 flex items-end gap-2">
                <textarea value={input} onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setInput(e.target.value)} onKeyDown={onKeyDown} rows={1} placeholder={L('typeMsg')} className="flex-1 px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:bg-white resize-none max-h-32" />
                <button onClick={send} disabled={sending || !input.trim()} className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 disabled:opacity-40 flex-shrink-0"><IconSend /></button>
              </div>
            </>
          )}
        </div>
      </div>
    </Backdrop>
  );
};

const EmailOverlay = ({ L, token, onClose }: OverlayProps) => {
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [selected, setSelected] = useState<Set<string>>(() => new Set<string>());
  const [search, setSearch] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [flash, setFlash] = useState('');

  useEffect(() => {
    fetch(`${ALG}/bulkemail/recipients?token=${token}`)
      .then((r) => { if (r.status === 403) { setAllowed(false); return []; } setAllowed(true); return r.ok ? r.json() : []; })
      .then((d: Recipient[]) => setRecipients(Array.isArray(d) ? d : [])).catch(() => setAllowed(false));
  }, [token]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? recipients.filter((r) => fullName(r).toLowerCase().includes(q) || (r.email ?? '').toLowerCase().includes(q)) : recipients;
  }, [recipients, search]);
  const allSel = filtered.length > 0 && filtered.every((r) => selected.has(r.id));
  const toggle = (id: string) => setSelected((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleAll = () => setSelected((p) => { const n = new Set(p); if (allSel) filtered.forEach((r) => n.delete(r.id)); else filtered.forEach((r) => n.add(r.id)); return n; });

  const send = () => {
    if (!subject.trim() || !message.trim()) { setFlash(L('errFields')); return; }
    if (selected.size === 0) { setFlash(L('errPick')); return; }
    setSending(true); setFlash('');
    fetch(`${ALG}/bulkemail/send?token=${token}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ subject: subject.trim(), message: message.trim(), recipientIds: Array.from(selected) }) })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((res: { sent?: number }) => { setFlash(`${L('sentOk')} (${res.sent ?? 0})`); setSubject(''); setMessage(''); setSelected(new Set<string>()); })
      .catch(() => setFlash(L('errSend'))).finally(() => setSending(false));
  };

  return (
    <Backdrop onClose={onClose}>
      <div className="w-full max-w-4xl max-h-[88vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <div><h2 className="text-base font-bold text-gray-800">{L('email')}</h2><p className="text-xs text-gray-500">{L('emailSubtitle')}</p></div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg text-gray-500 hover:bg-gray-100 text-lg">✕</button>
        </div>
        {allowed === false ? (
          <div className="p-10 text-center text-sm text-gray-500">{L('noAccess')}</div>
        ) : (
          <>
            {flash && <div className="mx-4 mt-3 px-3 py-2 rounded-lg bg-blue-50 text-blue-700 text-sm">{flash}</div>}
            <div className="flex-1 overflow-y-auto grid grid-cols-1 lg:grid-cols-2 gap-4 p-4">
              <div className="flex flex-col gap-3">
                <input value={subject} onChange={(e: ChangeEvent<HTMLInputElement>) => setSubject(e.target.value)} placeholder={L('subjectPh')} className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:bg-white" />
                <textarea value={message} onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setMessage(e.target.value)} placeholder={L('messagePh')} rows={10} className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:bg-white resize-y" />
              </div>
              <div className="flex flex-col border border-gray-100 rounded-xl overflow-hidden">
                <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
                  <span className="text-xs font-medium text-gray-500">{L('recipients')} · {selected.size}</span>
                  <button onClick={toggleAll} className="text-xs text-blue-600 hover:text-blue-800 font-medium">{allSel ? L('clear') : L('selectAll')}</button>
                </div>
                <input value={search} onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)} placeholder={L('searchPeople')} className="m-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:bg-white" />
                <div className="flex-1 overflow-y-auto max-h-72 divide-y divide-gray-50">
                  {allowed === null ? <div className="px-4 py-8 text-center text-sm text-gray-400">…</div> :
                    filtered.length === 0 ? <div className="px-4 py-8 text-center text-sm text-gray-400">{L('noRecipients')}</div> :
                      filtered.map((r) => (
                        <label key={r.id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 cursor-pointer">
                          <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} className="w-4 h-4 accent-blue-600" />
                          <span className="min-w-0 flex-1"><span className="block text-sm text-gray-800 truncate">{fullName(r)}</span><span className="block text-xs text-gray-400 truncate">{r.email}</span></span>
                        </label>
                      ))}
                </div>
              </div>
            </div>
            <div className="p-3 border-t border-gray-100 flex justify-end">
              <button onClick={send} disabled={sending} className="px-6 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"><IconSend />{sending ? L('sending') : L('send')}</button>
            </div>
          </>
        )}
      </div>
    </Backdrop>
  );
};


// ------------------------------------------------------- Help / support ticket overlay
// Hesabı olan HER kullanıcı buradan bir "Help Ticket" (bug bildirimi / soru / öneri)
// açar VE kendi eski biletlerini durumlarıyla birlikte görür. Destek ekibi (agent —
// e-mail'i sunucudaki SUPPORT_AGENT_EMAILS listesinde olanlar) TÜM şirketlerin
// biletlerini görür ve durumu elle günceller. Backend: algebra-api `/api/support/*`.
interface SupportFile { id: string; fileName: string; mimeType?: string; size?: number; }
interface SupportTicket {
  id: string; ref: string; companyName?: string; accountName?: string; accountEmail?: string;
  category?: string; subject: string; message?: string; app?: string; pageUrl?: string;
  status: string; statusNote?: string; statusBy?: string; statusAt?: string; createdAt: string;
  files?: SupportFile[];
}

const TICKET_EXT = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'pdf'];
const TICKET_MAX_FILES = 6;
const TICKET_MAX_SIZE = 8 * 1024 * 1024;

// Durum anahtarları backend'deki (models/support) değerlerle BİREBİR aynı olmalı.
const STATUS_ORDER = ['sent', 'read', 'contact', 'in_progress', 'resolved', 'closed'];
const STATUS_STYLE: Record<string, string> = {
  sent: 'bg-gray-100 text-gray-600',
  read: 'bg-indigo-50 text-indigo-700',
  contact: 'bg-amber-50 text-amber-700',
  in_progress: 'bg-blue-50 text-blue-700',
  resolved: 'bg-green-50 text-green-700',
  closed: 'bg-gray-100 text-gray-500',
};
const STATUS_KEY: Record<string, string> = {
  sent: 'stSent', read: 'stRead', contact: 'stContact',
  in_progress: 'stInProgress', resolved: 'stResolved', closed: 'stClosed',
};
const CATEGORY_KEY: Record<string, string> = { bug: 'typeBug', question: 'typeQuestion', feature: 'typeFeature', other: 'typeOther' };

// appName, biletin hangi uygulamadan açıldığını host'tan türetir (mail'de görünür).
const appName = (): string => {
  const h = window.location.hostname || '';
  if (h.startsWith('algebra.')) return 'Algebra';
  if (h.startsWith('esd.')) return 'Documentology';
  if (h.startsWith('architecture.')) return 'Architecture';
  if (h.includes('isosofts.com')) return 'IsoSofts Suite';
  return h || 'IsoSofts';
};

// Suite genelinde tarih gösterimi gün/ay/yıl.
const fmtDateTime = (iso?: string): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

const StatusBadge = ({ L, status }: { L: (k: string) => string; status: string }) => (
  <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${STATUS_STYLE[status] ?? STATUS_STYLE.sent}`}>
    {L(STATUS_KEY[status] ?? 'stSent')}
  </span>
);

const HelpOverlay = ({ L, token, onClose }: OverlayProps) => {
  const [tab, setTab] = useState<'new' | 'list'>('new');
  const [scope, setScope] = useState<'mine' | 'all'>('mine');
  const [isAgent, setIsAgent] = useState(false);
  const [tickets, setTickets] = useState<SupportTicket[] | null>(null);
  const [detail, setDetail] = useState<SupportTicket | null>(null);

  const [category, setCategory] = useState('bug');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const [flash, setFlash] = useState('');
  const [done, setDone] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  // Görsel önizlemeleri — File → blob URL eşlemesi. Map sayesinde aynı dosya için
  // URL bir kez üretilir (StrictMode çift-render'ında da güvenli); serbest bırakma
  // effect'e değil, kaldır/gönder/kapat eylemlerine bağlıdır.
  const urlsRef = useRef<Map<File, string>>(new Map());
  const previewOf = (f: File): string => {
    if (!f.type.startsWith('image/')) return '';
    let u = urlsRef.current.get(f);
    if (!u) { u = URL.createObjectURL(f); urlsRef.current.set(f, u); }
    return u;
  };
  const revokeOne = (f: File) => { const u = urlsRef.current.get(f); if (u) { URL.revokeObjectURL(u); urlsRef.current.delete(f); } };
  const revokeAll = () => { urlsRef.current.forEach((u) => URL.revokeObjectURL(u)); urlsRef.current.clear(); };
  const close = () => { revokeAll(); onClose(); };

  useEffect(() => {
    fetch(`${ALG}/support/me?token=${token}`).then((r) => (r.ok ? r.json() : { isAgent: false }))
      .then((d: { isAgent?: boolean }) => setIsAgent(!!d?.isAgent)).catch(() => undefined);
  }, [token]);

  const loadTickets = useCallback(() => {
    setTickets(null);
    fetch(`${ALG}/support/ticket?token=${token}&scope=${scope}`).then((r) => (r.ok ? r.json() : []))
      .then((d: SupportTicket[]) => setTickets(Array.isArray(d) ? d : [])).catch(() => setTickets([]));
  }, [token, scope]);

  useEffect(() => { if (tab === 'list') loadTickets(); }, [tab, loadTickets]);

  const openDetail = (t: SupportTicket) => {
    setDetail(t); // önce elimizdeki özet, sonra dosyalarıyla tam kayıt
    fetch(`${ALG}/support/ticket/${t.id}?token=${token}`).then((r) => (r.ok ? r.json() : null))
      .then((d: SupportTicket | null) => { if (d?.id) setDetail(d); }).catch(() => undefined);
  };

  // Durum güncellendiğinde hem detayı hem listedeki satırı tazele.
  const applyStatus = (res: Partial<SupportTicket>) => {
    setDetail((p) => (p ? { ...p, ...res } : p));
    setTickets((p) => (p ? p.map((t) => (detail && t.id === detail.id ? { ...t, ...res } : t)) : p));
  };

  const addFiles = (picked: File[]) => {
    const next = [...files];
    for (const f of picked) {
      if (next.length >= TICKET_MAX_FILES) { setFlash(L('ticketErrMax')); break; }
      const ext = (f.name.split('.').pop() ?? '').toLowerCase();
      if (!TICKET_EXT.includes(ext)) { setFlash(`${L('ticketErrType')}: ${f.name}`); continue; }
      if (f.size > TICKET_MAX_SIZE) { setFlash(`${L('ticketErrSize')}: ${f.name}`); continue; }
      next.push(f);
    }
    setFiles(next);
    if (fileRef.current) fileRef.current.value = '';
  };
  const removeFile = (i: number) => setFiles((p) => { revokeOne(p[i]); return p.filter((_, idx) => idx !== i); });

  const send = () => {
    if (!subject.trim() || !message.trim()) { setFlash(L('ticketErrFields')); return; }
    setSending(true); setFlash('');
    const fd = new FormData();
    fd.append('category', category);
    fd.append('subject', subject.trim());
    fd.append('message', message.trim());
    fd.append('app', appName());
    fd.append('pageUrl', window.location.href);
    files.forEach((f) => fd.append('files', f));
    // Content-Type ELLE verilmez — tarayıcı multipart boundary'sini kendi ekler.
    fetch(`${ALG}/support/ticket?token=${token}`, { method: 'POST', body: fd })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((res: { ticketId?: string }) => { setDone(res?.ticketId ?? '—'); setSubject(''); setMessage(''); setFiles([]); revokeAll(); })
      .catch((r: { status?: number }) => setFlash(r?.status === 429 ? L('ticketRate') : L('ticketErr')))
      .finally(() => setSending(false));
  };

  const startNew = () => { setDone(''); setDetail(null); setTab('new'); };
  // goList, hem sekmeyi hem kapsamı (kendi biletlerim / tümü) birlikte kurar —
  // böylece iki sekme düğmesi asla aynı anda aktif görünmez.
  const goList = (next: 'mine' | 'all') => { setDone(''); setDetail(null); setScope(next); setTab('list'); };

  const categories: [string, string][] = [
    ['bug', L('typeBug')], ['question', L('typeQuestion')], ['feature', L('typeFeature')], ['other', L('typeOther')],
  ];
  const tabClass = (active: boolean) =>
    `px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${active ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`;

  return (
    <Backdrop onClose={close}>
      <div className="w-full max-w-3xl max-h-[88vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-gray-100 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-base font-bold text-gray-800">{L('help')}</h2>
            <p className="text-xs text-gray-500">{L('helpSubtitle')}</p>
          </div>
          <button onClick={close} className="w-8 h-8 rounded-lg text-gray-500 hover:bg-gray-100 text-lg flex-shrink-0">✕</button>
        </div>

        <div className="px-4 py-2 border-b border-gray-100 flex items-center gap-2 flex-wrap">
          <button onClick={startNew} className={tabClass(tab === 'new')}>{L('tabNew')}</button>
          <button onClick={() => goList('mine')} className={tabClass(tab === 'list' && scope === 'mine')}>{L('tabMine')}</button>
          {isAgent && (
            <>
              <span className="w-px h-4 bg-gray-200 mx-1" />
              <button onClick={() => goList('all')} className={tabClass(tab === 'list' && scope === 'all')}>{L('tabAll')}</button>
            </>
          )}
        </div>

        {tab === 'list' ? (
          detail ? (
            <TicketDetail L={L} token={token} ticket={detail} isAgent={isAgent} onBack={() => setDetail(null)} onChanged={applyStatus} />
          ) : (
            <div className="flex-1 overflow-y-auto">
              {tickets === null ? (
                <div className="px-4 py-10 text-center text-sm text-gray-400">…</div>
              ) : tickets.length === 0 ? (
                <div className="px-4 py-10 text-center text-sm text-gray-400">{L('listEmpty')}</div>
              ) : (
                <div className="divide-y divide-gray-50">
                  {tickets.map((t) => (
                    <button key={t.id} onClick={() => openDetail(t)} className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-start gap-3">
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2 flex-wrap">
                          <span className="text-[11px] font-bold text-blue-600">{t.ref}</span>
                          <StatusBadge L={L} status={t.status} />
                          <span className="text-[11px] text-gray-400">{L(CATEGORY_KEY[t.category ?? 'other'] ?? 'typeOther')}</span>
                        </span>
                        <span className="block text-sm font-semibold text-gray-800 truncate mt-0.5">{t.subject}</span>
                        <span className="block text-[11px] text-gray-400 truncate">
                          {fmtDateTime(t.createdAt)}
                          {scope === 'all' && t.accountName ? ` · ${t.accountName}${t.companyName ? ' · ' + t.companyName : ''}` : ''}
                        </span>
                      </span>
                      <span className="text-gray-300 text-lg leading-none mt-1">›</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )
        ) : done ? (
          <div className="p-10 text-center">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-green-50 text-green-600 flex items-center justify-center text-2xl">✓</div>
            <p className="text-sm text-gray-700">{L('ticketOk')} <span className="font-bold text-gray-900">{done}</span></p>
            <div className="mt-6 flex items-center justify-center gap-2">
              <button onClick={() => goList('mine')} className="px-5 py-2.5 rounded-xl bg-gray-100 text-gray-700 text-sm font-semibold hover:bg-gray-200">{L('tabMine')}</button>
              <button onClick={close} className="px-6 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700">OK</button>
            </div>
          </div>
        ) : (
          <>
            {flash && <div className="mx-4 mt-3 px-3 py-2 rounded-lg bg-amber-50 text-amber-700 text-sm">{flash}</div>}
            <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-3">
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1.5">{L('ticketType')}</p>
                <div className="flex flex-wrap gap-2">
                  {categories.map(([key, label]) => (
                    <button key={key} onClick={() => setCategory(key)}
                      className={`px-3 py-1.5 rounded-xl text-xs font-semibold border transition-colors ${category === key ? 'bg-blue-600 text-white border-blue-600' : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'}`}>{label}</button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1.5">{L('ticketSubject')}</p>
                <input value={subject} onChange={(e: ChangeEvent<HTMLInputElement>) => setSubject(e.target.value)} maxLength={200} placeholder={L('ticketSubjectPh')} className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:bg-white" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1.5">{L('ticketMessage')}</p>
                <textarea value={message} onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setMessage(e.target.value)} maxLength={8000} rows={7} placeholder={L('ticketMessagePh')} className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:bg-white resize-y" />
              </div>
              <div className="border border-gray-200 rounded-xl p-3">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="text-xs font-medium text-gray-500">{L('ticketFiles')} · {files.length}/{TICKET_MAX_FILES}</span>
                  <button onClick={() => fileRef.current?.click()} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 text-blue-600 text-xs font-semibold hover:bg-blue-100"><IconClip />{L('ticketAddFiles')}</button>
                </div>
                <input ref={fileRef} type="file" multiple accept="image/png,image/jpeg,image/gif,image/bmp,application/pdf" className="hidden"
                  onChange={(e: ChangeEvent<HTMLInputElement>) => addFiles(Array.from(e.target.files ?? []))} />
                {files.length === 0 ? (
                  <p className="text-[11px] text-gray-400">{L('ticketFilesHint')}</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {files.map((f, i) => (
                      <span key={`${f.name}-${i}`} className="relative flex items-center gap-2 pl-1.5 pr-7 py-1.5 rounded-lg bg-gray-50 border border-gray-200 max-w-[220px]">
                        {previewOf(f)
                          ? <img src={previewOf(f)} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0" />
                          : <span className="w-8 h-8 rounded bg-gray-200 text-gray-500 text-[9px] font-bold flex items-center justify-center flex-shrink-0">PDF</span>}
                        <span className="text-xs text-gray-700 truncate">{f.name}</span>
                        <button onClick={() => removeFile(i)} title={L('ticketRemove')} className="absolute right-1 top-1/2 -translate-y-1/2 w-5 h-5 rounded text-gray-400 hover:text-red-500 text-xs">✕</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-[11px] text-gray-400">{L('ticketWho')}</p>
            </div>
            <div className="p-3 border-t border-gray-100 flex justify-end">
              <button onClick={send} disabled={sending} className="px-6 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"><IconSend />{sending ? L('ticketSending') : L('ticketSend')}</button>
            </div>
          </>
        )}
      </div>
    </Backdrop>
  );
};

// Tek bir biletin detayı. Agent ise altta durum güncelleme bloğu görünür.
const TicketDetail = ({ L, token, ticket, isAgent, onBack, onChanged }: {
  L: (k: string) => string;
  token: string;
  ticket: SupportTicket;
  isAgent: boolean;
  onBack: () => void;
  onChanged: (res: Partial<SupportTicket>) => void;
}) => {
  const [status, setStatus] = useState(ticket.status || 'sent');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState('');

  useEffect(() => { setStatus(ticket.status || 'sent'); }, [ticket.status]);

  const save = () => {
    setSaving(true); setFlash('');
    fetch(`${ALG}/support/ticket/${ticket.id}/status?token=${token}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status, note: note.trim() }),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((res: Partial<SupportTicket>) => { setFlash(L('agentSaved')); setNote(''); onChanged(res); })
      .catch(() => setFlash(L('ticketErr')))
      .finally(() => setSaving(false));
  };

  const row = (label: string, value?: string) =>
    value ? (
      <div className="flex gap-2 text-xs">
        <span className="text-gray-400 w-28 flex-shrink-0">{label}</span>
        <span className="text-gray-700 break-words min-w-0">{value}</span>
      </div>
    ) : null;

  return (
    <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-3">
      <button onClick={onBack} className="self-start text-xs text-blue-600 hover:text-blue-800 font-semibold">‹ {L('detailBack')}</button>

      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] font-bold text-blue-600">{ticket.ref}</span>
        <StatusBadge L={L} status={ticket.status} />
        <span className="text-[11px] text-gray-400">{L(CATEGORY_KEY[ticket.category ?? 'other'] ?? 'typeOther')}</span>
      </div>
      <h3 className="text-base font-bold text-gray-800 -mt-1">{ticket.subject}</h3>

      <div className="bg-gray-50 border border-gray-100 rounded-xl p-3 text-sm text-gray-700 whitespace-pre-wrap break-words">{ticket.message}</div>

      {ticket.files && ticket.files.length > 0 && (
        <div>
          <p className="text-xs font-medium text-gray-500 mb-1.5">{L('ticketFiles')}</p>
          <div className="flex flex-wrap gap-2">
            {ticket.files.map((f) => (
              <a key={f.id} href={`${ALG}/support/file/${f.id}?token=${token}`} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 text-xs font-semibold hover:bg-blue-100 max-w-[220px]">
                <IconClip /><span className="truncate">{f.fileName}</span>
              </a>
            ))}
          </div>
        </div>
      )}

      <div className="border-t border-gray-100 pt-3 flex flex-col gap-1.5">
        {row(L('detailOpened'), fmtDateTime(ticket.createdAt))}
        {row(L('detailReporter'), ticket.accountName)}
        {row(L('detailEmail'), ticket.accountEmail)}
        {row(L('detailCompany'), ticket.companyName)}
        {row(L('detailApp'), ticket.app)}
        {row(L('detailPage'), ticket.pageUrl)}
        {row(L('detailUpdatedBy'), ticket.statusBy ? `${ticket.statusBy} · ${fmtDateTime(ticket.statusAt)}` : '')}
      </div>

      {ticket.statusNote && (
        <div className="bg-blue-50/60 border border-blue-100 rounded-xl p-3">
          <p className="text-[11px] font-semibold text-blue-700 mb-1">{L('detailStatusNote')}</p>
          <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">{ticket.statusNote}</p>
        </div>
      )}

      {isAgent && (
        <div className="border border-gray-200 rounded-xl p-3 flex flex-col gap-2">
          <p className="text-xs font-semibold text-gray-600">{L('agentUpdate')}</p>
          {flash && <div className="px-3 py-2 rounded-lg bg-blue-50 text-blue-700 text-xs">{flash}</div>}
          <div className="flex flex-wrap gap-2">
            {STATUS_ORDER.map((s) => (
              <button key={s} onClick={() => setStatus(s)}
                className={`px-3 py-1.5 rounded-xl text-xs font-semibold border transition-colors ${status === s ? 'bg-blue-600 text-white border-blue-600' : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'}`}>
                {L(STATUS_KEY[s])}
              </button>
            ))}
          </div>
          <textarea value={note} onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setNote(e.target.value)} rows={3} maxLength={2000} placeholder={L('agentNotePh')} className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:bg-white resize-y" />
          <p className="text-[11px] text-gray-400">{L('agentMailHint')}</p>
          <button onClick={save} disabled={saving} className="self-end px-5 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
            {saving ? L('ticketSending') : L('agentSave')}
          </button>
        </div>
      )}
    </div>
  );
};

export default SuiteTools;
